import { useQuery } from '@tanstack/react-query';
import { booking } from '@lawbee/domain';
import { Alert, Badge, Button, Checkbox, Dialog, DialogContent, EmptyState, ErrorState, Field, FormError, Input, LoadingState, Select, Textarea, formatMoney } from '@lawbee/ui';
import { CalendarClock, Pencil, Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dollarsToCents, useApiMutation } from '../../lib/hooks';
import { can, hasFeature, useMe } from '../../lib/me';
import type { AvailabilityRule, BookingType } from '../../lib/types';
import { AvailabilityEditor, CopyButton, ReadOnlyNote, sortRules } from '../app/ops/shared';
import { SettingsCard } from './layout';

const TYPES_KEY = ['booking-types'] as const;
const AVAIL_KEY = ['booking-availability'] as const;

const KIND_OPTIONS = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow-up meeting' },
];

function kindLabel(kind: string): string {
  return KIND_OPTIONS.find((k) => k.value === kind)?.label ?? kind.replace(/_/g, ' ');
}

export function BookingSettingsPage() {
  const me = useMe();
  const canEdit = can(me, 'settings');
  return (
    <div className="space-y-6">
      {!canEdit ? <ReadOnlyNote /> : null}
      <BookingTypesCard canEdit={canEdit} paymentsOn={hasFeature(me, 'payments')} />
      <AvailabilityCard canEdit={canEdit} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking types
// ---------------------------------------------------------------------------

function BookingTypesCard({ canEdit, paymentsOn }: { canEdit: boolean; paymentsOn: boolean }) {
  const q = useQuery({ queryKey: TYPES_KEY, queryFn: () => api.get<{ types: BookingType[] }>('/api/booking/types') });
  const [editing, setEditing] = useState<BookingType | 'new' | null>(null);

  return (
    <SettingsCard
      title="Booking types"
      description="What clients can book on your public booking page, how long it takes and what it costs."
      footer={
        canEdit ? (
          <Button onClick={() => setEditing('new')}>
            <Plus /> New booking type
          </Button>
        ) : undefined
      }
    >
      {q.isLoading ? <LoadingState label="Loading booking types" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.types.length === 0 ? (
        <EmptyState icon={<CalendarClock />} title="No booking types yet" description="Add a free or paid consultation so clients can book online." className="py-8" />
      ) : null}
      {q.data && q.data.types.length > 0 ? (
        <ul className="divide-y divide-border/70">
          {q.data.types.map((t) => (
            <li key={t.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{t.name}</span>
                  <Badge variant="outline">{kindLabel(t.kind)}</Badge>
                  {!t.active ? <Badge>Inactive</Badge> : null}
                </div>
                <p className="text-[13px] text-muted-foreground">
                  <span className="figures">{t.durationMin} min</span>
                  {t.bufferMin ? (
                    <>
                      {' '}
                      + <span className="figures">{t.bufferMin} min</span> buffer
                    </>
                  ) : null}
                  {' · '}
                  {t.priceCents > 0 ? <span className="figures">{formatMoney(t.priceCents)}</span> : 'Free'}
                  {' · '}
                  <span className="figures">{t.minNoticeHours}</span> h notice, up to <span className="figures">{t.maxDaysAhead}</span> days ahead
                </p>
                {t.description ? <p className="text-[13px] text-muted-foreground">{t.description}</p> : null}
                <p className="figures truncate text-xs text-muted-foreground">{t.publicUrl}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <CopyButton value={t.publicUrl} label="Copy link" copiedLabel="Booking link copied" />
                {canEdit ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(t)} aria-label={`Edit ${t.name}`}>
                    <Pencil /> Edit
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing ? <BookingTypeForm type={editing === 'new' ? null : editing} paymentsOn={paymentsOn} onClose={() => setEditing(null)} /> : null}
      </Dialog>
    </SettingsCard>
  );
}

function BookingTypeForm({ type, paymentsOn, onClose }: { type: BookingType | null; paymentsOn: boolean; onClose: () => void }) {
  const [name, setName] = useState(type?.name ?? '');
  const [kind, setKind] = useState(type?.kind ?? 'consultation');
  const [description, setDescription] = useState(type?.description ?? '');
  const [duration, setDuration] = useState(String(type?.durationMin ?? 30));
  const [buffer, setBuffer] = useState(String(type?.bufferMin ?? 0));
  const [price, setPrice] = useState(type ? (type.priceCents / 100).toFixed(2) : '0');
  const [notice, setNotice] = useState(String(type?.minNoticeHours ?? 2));
  const [ahead, setAhead] = useState(String(type?.maxDaysAhead ?? 30));
  const [active, setActive] = useState(type?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useApiMutation(
    (body: Record<string, unknown>) => (type ? api.patch(`/api/booking/types/${type.id}`, body) : api.post('/api/booking/types', body)),
    { success: type ? 'Booking type saved' : 'Booking type created', invalidate: [TYPES_KEY], onSuccess: onClose },
  );

  const priceCents = dollarsToCents(price);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const d = Number(duration);
    const b = Number(buffer);
    const n = Number(notice);
    const a = Number(ahead);
    if (name.trim().length < 2) return setError('Name must be at least 2 characters.');
    if (!Number.isInteger(d) || d < 10 || d > 480) return setError('Duration must be 10 to 480 minutes.');
    if (!Number.isInteger(b) || b < 0 || b > 120) return setError('Buffer must be 0 to 120 minutes.');
    if (priceCents < 0) return setError('Price cannot be negative.');
    if (!Number.isInteger(n) || n < 0 || n > 336) return setError('Minimum notice must be 0 to 336 hours.');
    if (!Number.isInteger(a) || a < 1 || a > 365) return setError('Booking window must be 1 to 365 days.');
    setError(null);
    save.mutate(
      { name: name.trim(), kind, description: description.trim() || null, durationMin: d, bufferMin: b, priceCents, minNoticeHours: n, maxDaysAhead: a, active },
      { onError: (err) => setError(errorMessage(err)) },
    );
  };

  return (
    <DialogContent
      title={type ? `Edit ${type.name}` : 'New booking type'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="booking-type-form" loading={save.isPending}>
            {type ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="booking-type-form" onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="bt-name" required>
            <Input id="bt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Initial consultation" />
          </Field>
          <Field label="Kind" htmlFor="bt-kind">
            <Select id="bt-kind" value={kind} onChange={(e) => setKind(e.target.value)} options={KIND_OPTIONS.some((k) => k.value === kind) ? KIND_OPTIONS : [...KIND_OPTIONS, { value: kind, label: kind }]} />
          </Field>
        </div>
        <Field label="Description" htmlFor="bt-desc" description="Shown to clients on the booking page.">
          <Textarea id="bt-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
        </Field>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Duration (min)" htmlFor="bt-dur">
            <Input id="bt-dur" type="number" inputMode="numeric" min={10} max={480} step={5} className="figures" value={duration} onChange={(e) => setDuration(e.target.value)} />
          </Field>
          <Field label="Buffer after (min)" htmlFor="bt-buf">
            <Input id="bt-buf" type="number" inputMode="numeric" min={0} max={120} step={5} className="figures" value={buffer} onChange={(e) => setBuffer(e.target.value)} />
          </Field>
          <Field label="Price (CAD)" htmlFor="bt-price" description="0 for free">
            <Input id="bt-price" inputMode="decimal" className="figures" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Field label="Minimum notice (hours)" htmlFor="bt-notice">
            <Input id="bt-notice" type="number" inputMode="numeric" min={0} className="figures" value={notice} onChange={(e) => setNotice(e.target.value)} />
          </Field>
          <Field label="Book up to (days ahead)" htmlFor="bt-ahead">
            <Input id="bt-ahead" type="number" inputMode="numeric" min={1} max={365} className="figures" value={ahead} onChange={(e) => setAhead(e.target.value)} />
          </Field>
        </div>
        {priceCents > 0 ? (
          <Alert variant="info">
            Clients pay when they book. Paid consultations follow your consultation fee routing (Settings, Payments).
            {!paymentsOn ? ' Payments are not included in your plan, so this booking cannot be paid online.' : ''}
          </Alert>
        ) : null}
        <Checkbox label="Active (shown on your booking page)" checked={active} onCheckedChange={(v) => setActive(v === true)} />
        <FormError message={error} />
      </form>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// Weekly availability
// ---------------------------------------------------------------------------

function AvailabilityCard({ canEdit }: { canEdit: boolean }) {
  const q = useQuery({ queryKey: AVAIL_KEY, queryFn: () => api.get<{ rules: AvailabilityRule[]; timezone: string }>('/api/booking/availability') });
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) setRules(sortRules(q.data.rules));
  }, [q.data]);

  const save = useApiMutation((body: { rules: AvailabilityRule[]; timezone: string }) => api.put('/api/booking/availability', body), {
    success: 'Availability saved',
    invalidate: [AVAIL_KEY],
  });

  const dirty = q.data ? JSON.stringify(sortRules(q.data.rules)) !== JSON.stringify(rules) : false;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!q.data) return;
    const problems = booking.validateRules(rules);
    if (problems.length) return setError(problems[0] ?? 'Check your hours.');
    for (const d of new Set(rules.map((r) => r.weekday))) {
      const day = rules.filter((r) => r.weekday === d).sort((a, b) => a.startMin - b.startMin);
      for (let i = 1; i < day.length; i++) {
        const prev = day[i - 1];
        const cur = day[i];
        if (prev && cur && cur.startMin < prev.endMin) return setError('Hours on the same day must not overlap.');
      }
    }
    setError(null);
    save.mutate({ rules, timezone: q.data.timezone }, { onError: (err) => setError(errorMessage(err)) });
  };

  return (
    <form onSubmit={submit} noValidate>
      <SettingsCard
        title="Weekly availability"
        description={
          q.data ? (
            <>
              When clients can book, in your firm&apos;s time zone (<span className="figures">{q.data.timezone}</span>). Existing bookings and buffers are excluded automatically.
            </>
          ) : (
            'When clients can book.'
          )
        }
        footer={
          canEdit && q.data ? (
            <>
              {dirty ? (
                <Button type="button" variant="ghost" onClick={() => setRules(sortRules(q.data.rules))}>
                  Discard
                </Button>
              ) : null}
              <Button type="submit" disabled={!dirty} loading={save.isPending}>
                Save availability
              </Button>
            </>
          ) : undefined
        }
      >
        {q.isLoading ? <LoadingState label="Loading availability" /> : null}
        {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
        {q.data ? (
          <div className="space-y-4">
            {rules.length === 0 ? <Alert variant="warning">No hours set. Clients cannot book until you open at least one day.</Alert> : null}
            <AvailabilityEditor rules={rules} onChange={setRules} disabled={!canEdit} />
            <FormError message={error} />
          </div>
        ) : null}
      </SettingsCard>
    </form>
  );
}

